import * as THREE from 'three';
import DryBush from './DryBush';

export default class Tumbleweed extends DryBush {
    constructor(scene, physics, position = { x: 0, z: 0 }, sizeFactor = 1) {
        super(scene, physics, position, sizeFactor);
        this.collider = null;
        this.velocity = new THREE.Vector3();
        this.angularVelocity = new THREE.Vector3();
        this.isGrounded = true;
        this.mass = 1;
        this.friction = 0.98;
        this.bounciness = 0.5;
        
        // Create the tumbleweed
        this.create();
    }
    
    create() {
        // Base size with variation
        this.size = (0.3 + Math.random() * 0.4) * this.sizeFactor;
        
        // Create tumbleweed material with dry, weathered appearance
        const hue = 0.08 + (Math.random() * 0.05);
        const saturation = 0.25 + (Math.random() * 0.15);
        const lightness = 0.2 + (Math.random() * 0.1);
        const color = new THREE.Color().setHSL(hue, saturation, lightness);
        
        const tumbleWeedMaterial = new THREE.MeshStandardMaterial({
            color: color,
            roughness: 0.9,
            metalness: 0.1,
            flatShading: true,
            wireframe: true
        });
        
        // Create spherical base with spikes
        const numSpikes = 15 + Math.floor(Math.random() * 10);
        
        // Create base sphere
        const sphereGeometry = new THREE.SphereGeometry(this.size, 8, 6);
        const sphere = new THREE.Mesh(sphereGeometry, tumbleWeedMaterial);
        
        // Store original vertices for wind animation
        const originalPositions = new Float32Array(sphereGeometry.attributes.position.array);
        this.originalVertices.push(originalPositions);
        
        this.bushGroup.add(sphere);
        
        // Add spikes
        for (let i = 0; i < numSpikes; i++) {
            const spikeLength = this.size * (0.3 + Math.random() * 0.4);
            const spikeGeometry = new THREE.ConeGeometry(spikeLength * 0.15, spikeLength, 4);
            
            // Store spike vertices
            const spikePositions = new Float32Array(spikeGeometry.attributes.position.array);
            this.originalVertices.push(spikePositions);
            
            const spike = new THREE.Mesh(spikeGeometry, tumbleWeedMaterial);
            
            // Random position on sphere
            const phi = Math.random() * Math.PI * 2;
            const theta = Math.random() * Math.PI;
            
            spike.position.set(
                this.size * Math.sin(theta) * Math.cos(phi),
                this.size * Math.sin(theta) * Math.sin(phi),
                this.size * Math.cos(theta)
            );
            
            // Point away from center
            spike.lookAt(new THREE.Vector3());
            spike.rotateX(Math.PI / 2);
            
            this.bushGroup.add(spike);
        }
        
        // Position the tumbleweed
        this.bushGroup.position.set(
            this.position.x,
            this.size,  // Start slightly above ground
            this.position.z
        );
        
        this.bushGroup.castShadow = true;
        this.bushGroup.receiveShadow = true;
        
        this.scene.add(this.bushGroup);
    }
    
    update(deltaTime) {
        // Apply wind force
        const windForce = new THREE.Vector3(
            Math.sin(DryBush.windTime * DryBush.windFrequency) * DryBush.windStrength,
            0,
            Math.cos(DryBush.windTime * DryBush.windFrequency) * DryBush.windStrength
        );
        
        // Apply forces
        this.velocity.add(windForce.multiplyScalar(deltaTime * 2));
        
        // Ground check and bounce
        if (this.bushGroup.position.y <= this.size) {
            this.bushGroup.position.y = this.size;
            this.velocity.y = Math.abs(this.velocity.y) * this.bounciness;
            this.isGrounded = true;
        } else {
            this.velocity.y -= 9.8 * deltaTime; // Gravity
            this.isGrounded = false;
        }
        
        // Apply friction when grounded
        if (this.isGrounded) {
            this.velocity.x *= this.friction;
            this.velocity.z *= this.friction;
        }
        
        // Update position
        this.bushGroup.position.add(this.velocity.multiplyScalar(deltaTime));
        
        // Calculate rotation based on movement
        const speed = this.velocity.length();
        if (speed > 0.01) {
            const rotationAxis = new THREE.Vector3(-this.velocity.z, 0, this.velocity.x).normalize();
            this.bushGroup.rotateOnAxis(rotationAxis, speed * deltaTime);
        }
        
        // Apply wind effect to vertices
        super.update(deltaTime);
    }
    
    remove() {
        super.remove();
        if (this.physics && this.collider) {
            this.physics.removeBody(this.collider);
        }
    }
}